"use client"

import { MENU_ROLES, type MenuRole } from "@repo/types"
import { Lock, RotateCcw, Save } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { DendromapLoader } from "../../components/dendromap-loader"
import {
  createAdministrationItems,
  createConfigurationItems,
  createNavigationItems,
  LOCKED_MENU_HREFS,
  type MenuItem,
} from "../../components/sidebar-menu"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Checkbox } from "../../components/ui/checkbox"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table"
import { useMenuConfig, useUpdateMenuConfig } from "../../hooks/api/settings/use-menu-config"
import { useLabels } from "../../hooks/use-labels"
import { PageHeader } from "../../ui/layout/page-header"
import { buildMenuConfigFromDraft, buildMenuDraft, toggleDraftRole, type MenuDraft } from "./menu-config.helpers"

const ROLE_LABELS: Record<MenuRole, string> = {
  ADMIN: "Admin",
  DENDROMAP: "Dendromap",
  MANAGER: "Manager",
  AGENT: "Agent",
  USER: "Utilisateur",
  VISITEUR: "Visiteur",
}

/** Espace /superadmin : choisir, pour chaque menu de la barre latérale, les rôles qui le voient. */
export function MenuSettingsPage() {
  const { labels } = useLabels()
  const sections = useMemo(
    () => [
      { title: "Navigation", items: createNavigationItems(labels.sidebar) },
      { title: labels.sidebar.configuration.title, items: createConfigurationItems(labels.sidebar) },
      { title: labels.sidebar.administration.title, items: createAdministrationItems(labels.sidebar) },
    ],
    [labels],
  )
  const allItems = useMemo(() => sections.flatMap((section) => section.items), [sections])

  const { data: menuConfig, isLoading } = useMenuConfig()
  const updateMenuConfig = useUpdateMenuConfig()
  const [draft, setDraft] = useState<MenuDraft>({})

  useEffect(() => {
    if (menuConfig) setDraft(buildMenuDraft(allItems, menuConfig))
  }, [menuConfig, allItems])

  const save = async () => {
    try {
      await updateMenuConfig.mutateAsync(buildMenuConfigFromDraft(allItems, draft))
      toast.success("Menus enregistrés : la barre latérale est à jour pour tous les utilisateurs.")
    } catch {
      toast.error("Impossible d'enregistrer la configuration des menus.")
    }
  }

  const resetToDefaults = () => setDraft(buildMenuDraft(allItems, {}))

  if (isLoading) return <DendromapLoader message="Chargement des menus..." />

  const renderRow = (item: MenuItem, isSubItem: boolean) => {
    const locked = LOCKED_MENU_HREFS.has(item.href)
    const roles = draft[item.href] ?? []
    return (
      <TableRow key={item.href}>
        <TableCell className={isSubItem ? "pl-10 text-muted-foreground" : "font-medium"}>
          <div className="flex items-center gap-2">
            <item.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span>{item.name}</span>
            {locked && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Lock className="h-3 w-3" /> toujours visible
              </span>
            )}
          </div>
        </TableCell>
        {MENU_ROLES.map((role) => (
          <TableCell key={role} className="text-center">
            <Checkbox
              aria-label={`${item.name} — ${ROLE_LABELS[role]}`}
              checked={roles.includes(role)}
              disabled={locked}
              onCheckedChange={(checked) => setDraft((prev) => toggleDraftRole(prev, item.href, role, checked === true))}
            />
          </TableCell>
        ))}
      </TableRow>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeader.Title>{labels.sidebar.superadmin.menus}</PageHeader.Title>
        <PageHeader.Description>
          Cochez les rôles qui voient chaque menu. Un menu décoché est retiré de la barre latérale et sa page
          n&apos;est plus accessible par son adresse pour ce rôle. Masquer un groupe masque aussi ses sous-menus.
        </PageHeader.Description>
      </PageHeader>

      {sections.map((section) => (
        <Card key={section.title}>
          <CardHeader>
            <CardTitle className="text-base">{section.title}</CardTitle>
            <CardDescription>{section.items.length} menus</CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Menu</TableHead>
                  {MENU_ROLES.map((role) => (
                    <TableHead key={role} className="text-center">
                      {ROLE_LABELS[role]}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {section.items.flatMap((item) => [
                  renderRow(item, false),
                  ...(item.subMenu ?? []).map((sub) => renderRow(sub, true)),
                ])}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={resetToDefaults} disabled={updateMenuConfig.isPending}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Valeurs par défaut
        </Button>
        <Button onClick={save} disabled={updateMenuConfig.isPending}>
          <Save className="mr-2 h-4 w-4" />
          {updateMenuConfig.isPending ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </div>
    </div>
  )
}
